import Button from "@material-ui/core/Button";
import ArrowForward from "@material-ui/icons/ArrowForward";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import LinearProgress from "@material-ui/core/LinearProgress";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";
import { Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
// import Assignment from "@material-ui/icons/Assignment";
import * as React from "react";
import { Link } from "react-router-dom";
import {
  ISanitizedCoach,
  ISurveyRespondentResponse,
  ResponseSource,
} from "src/services";
import Component from "../../components/Component";
import MobilePage from "../../components/MobilePage";
import withAxios, { IAxiosProps } from "../../components/withAxios";
import { IRespondentPagePassedDown } from "../RespondentPage";

const styles = (theme: Theme): Record<string, CSSProperties> => ({
  pageFontScope: {
    fontFamily: '"Inter", sans-serif',
    "& *": {
      fontFamily: '"Inter", sans-serif !important',
    },
  },
  coachName: {
    color: "#141428",
    fontSize: "16px",
    fontWeight: 400,
    letterSpacing: 0,
    lineHeight: 1.35,
    opacity: 0.75,
    "@media (max-width: 768px)": {
      fontSize: "15px",
    },
    "@media (max-width: 480px)": {
      fontSize: "14px",
    },
  },
  button: {
    backgroundColor: "#000000",
    borderRadius: "8px",
    boxShadow: "none",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 600,
    padding: "8px 16px",
    textTransform: "none",
    "@media (max-width: 768px)": {
      padding: "6px 10px 5px",
    },
    "&:hover": {
      backgroundColor: "#000000",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#9ba3b5",
      color: "#ffffff",
      opacity: 1,
    },
  },
  plainButtonLabel: {
    "& .MuiButton-label": {
      fontFamily: '"Inter", sans-serif',
      fontStyle: "normal",
      color: "#FFF",
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: 1.2,
      "@media (max-width: 768px)": {
        fontSize: "12px",
      },
      "&::before": {
        content: '""',
      },
      "&::after": {
        content: '""',
      },
    },
    "&.Mui-disabled .MuiButton-label": {
      color: "#ffffff",
      opacity: 1,
    },
  },
  infoStrip: {
    background: "#f5f5f5",
    borderBottom: "2px solid rgba(225, 229, 239, 0.50)",
    color: "#333 !important",
    fontSize: "17px",
    fontWeight: 500,
    lineHeight: "27px",
    margin: "-16px -16px 28px",
    padding: "14px",
    "@media (max-width: 768px)": {
      fontSize: "14px",
      lineHeight: "22px",
    },
  },
  sectionCard: {
    background: "rgba(253, 253, 253, 0.05)",
    border: "2px solid rgba(225, 229, 239, 0.50)",
    borderRadius: "14px",
    boxShadow: "none",
    marginBottom: "18px",
    padding: "14px 14px 12px",
  },
  sectionTitle: {
    color: "#141428",
    fontSize: "17px",
    fontWeight: 500,
    lineHeight: "normal",
    marginBottom: "8px",
    "@media (max-width: 768px)": {
      fontSize: "16px",
    },
    "@media (max-width: 480px)": {
      fontSize: "15px",
    },
  },
  textFieldRoot: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      background: "rgba(221, 221, 221, 0.20)",
      fontFamily: '"Inter", sans-serif',
      fontSize: "16px",
      height: "50px",
      color: "#141428",
      opacity: 0.75,
      "& fieldset": {
        borderColor: "#E1E5EF",
        borderWidth: 2,
      },
      "&:hover fieldset": {
        borderColor: "#E1E5EF",
        borderWidth: 2,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E1E5EF",
        borderWidth: 2,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "13px 14px",
      color: "#141428",
      opacity: 0.75,
    },
    "@media (max-width: 768px)": {
      "& .MuiOutlinedInput-root": {
        fontSize: "13px",
      },
      "& .MuiOutlinedInput-input": {
        fontSize: "13px",
      },
    },
    "& .MuiOutlinedInput-input::placeholder": {
      color: "#a7afbf",
      opacity: 1,
    },
  },
  actionBar: {
    alignItems: "center",
    background: "rgba(253, 253, 253, 0.50)",
    border: "2px solid rgba(225, 229, 239, 0.50)",
    borderWidth: "2px 0",
    display: "flex",
    justifyContent: "flex-end",
    margin: "8px -16px 0",
    padding: "22px 22px",
  },
  formControlRow: {
    height: 48,
    marginBottom: 0,
    marginTop: 0,
  },
  radio: {
    color: "#c9d1e0",
    padding: "6px 10px 6px 10px",
    "&.Mui-checked": {
      color: "#4b5877",
    },
  },
});

const getSeasonLabelFromStartDate = (startDate?: number): string => {
  if (typeof startDate !== "number" || Number.isNaN(startDate)) {
    return "Fall";
  }

  const month = new Date(startDate).getMonth();
  if (month >= 7 && month <= 10) {
    return "Fall";
  }
  if (month === 11 || month <= 1) {
    return "Winter";
  }
  return "Spring";
};

interface IChooseCoachPageProps {
  collectRespondentName: boolean;
  code: string;
  schoolData:any;
  respondentName: string;
  respondentNameChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
}

type IProps = IChooseCoachPageProps &
  IRespondentPagePassedDown &
  IAxiosProps &
  WithStyles<keyof ReturnType<typeof styles>>;

interface IInputInformationState {
  selectedCoachId?: number;
}

export default withStyles(styles)(
  withAxios(
    class ChooseCoachPage extends Component<IProps, IInputInformationState> {
      public readonly state = this.buildState({
        selectedCoachId: undefined,
      });

      public componentDidMount() {
        const { loadSurvey, code } = this.props;
        loadSurvey(code);
      }

      // render for both students and coaches
      public render() {
        const { responseSource, surveyResponse, schoolData, school } = this.props;
        const schoolName = schoolData?.name ?? school.name ?? "";
        const schoolLogo = schoolData?.logo1 ?? school.logo1 ?? "";
        const schoolLogo2 = schoolData?.logo2 ?? school.logo2 ?? "";
        const schoolTagline = schoolData?.tagline ?? school.tagline ?? "";
        const respondentTypeLabel = (() => {
          switch (responseSource) {
            case ResponseSource.student:
              return schoolData?.isAdSurvey ? "Coach" : "Student";
            case ResponseSource.parent:
              return "Parent";
            case ResponseSource.coach:
              return schoolData?.isAdSurvey ? "AD" : "Coach";
            default:
              return "";
          }
        })();
        const selectedCoach =
          surveyResponse?.coaches.find(
            (coach) => coach.id === this.state.selectedCoachId
          ) || surveyResponse?.coaches[0];
        const seasonLabel = getSeasonLabelFromStartDate(
          surveyResponse?.survey?.startDate
        );
        // render error message and link to go to code input
        if (
          surveyResponse !== undefined &&
          !surveyResponse.properAccess.properAccessCode
        ) {
          return (
            <div className={this.props.classes.pageFontScope}>
              <MobilePage
                header={schoolName}
                title="Error"
                subtitle={`${surveyResponse.properAccess.errorMessage}`}
                schoolLogo={schoolLogo}
                schoolLogo2={schoolLogo2}
                schoolName={schoolName}
                // headerIcon={<Assignment />}
                tagline={schoolTagline}
                showSurveyHeader={true}
                showSurveyProgress={false}
                compactSurveyLayout={true}
                sportName={surveyResponse?.sport?.name || ''}
                coachName={selectedCoach?.name || ''}
                seasonName={seasonLabel}
                headLine={respondentTypeLabel}
              >
                <Button
                  color="primary"
                  variant="contained"
                  component={Link}
                  {...({ to: `/${ResponseSource[responseSource]}` } as any)}
                >
                  Enter Code Page
                </Button>
              </MobilePage>
            </div>
          );
        }

        // const sportName = (() => {
        //   if (
        //     surveyResponse === undefined ||
        //     surveyResponse.sport === undefined
        //   ) {
        //     return ``;
        //   } else {
        //     return `${surveyResponse.sport.name}`;
        //   }
        // })();

        switch (responseSource) {
          case ResponseSource.student:
            return (
              <div className={this.props.classes.pageFontScope}>
                <MobilePage
                  header={schoolName}
                  title={schoolData?.isAdSurvey ? "Select your AD" : "Select your coach"}
                  subtitle=""
                  schoolLogo={schoolLogo}
                  schoolLogo2={schoolLogo2}
                  schoolName={schoolName}
                  // headerIcon={<Assignment />}
                  tagline={schoolTagline}
                  showSurveyHeader={true}
                  showSurveyProgress={false}
                  compactSurveyLayout={true}
                  sportName={surveyResponse?.sport?.name || ''}
                  coachName={selectedCoach?.name || ''}
                  seasonName={seasonLabel}
                  headLine={respondentTypeLabel}
                >
                  {this.renderCoachSelect()}
                </MobilePage>
              </div>
            );
          case ResponseSource.coach:
            return (
              <div className={this.props.classes.pageFontScope}>
                <MobilePage
                  header={schoolName}
                  title="Select your name"
                  subtitle="Self-survey is imperative for great leaders."
                  schoolLogo={schoolLogo}
                  schoolLogo2={schoolLogo2}
                  schoolName={schoolName}
                  // headerIcon={<Assignment />}
                  tagline={schoolTagline}
                  showSurveyHeader={true}
                  showSurveyProgress={false}
                  compactSurveyLayout={true}
                  sportName={surveyResponse?.sport?.name || ''}
                  coachName={selectedCoach?.name || ''}
                  seasonName={seasonLabel}
                  headLine={respondentTypeLabel}
                >
                  {this.renderCoachSelect()}
                </MobilePage>
              </div>
            );
          case ResponseSource.parent:
            return (
              <div className={this.props.classes.pageFontScope}>
                <MobilePage
                  header={schoolName}
                  title={schoolData?.isAdSurvey ? "Select an AD" : "Select a coach"}
                  subtitle=""
                  schoolLogo={schoolLogo}
                  schoolLogo2={schoolLogo2}
                  schoolName={schoolName}
                  // headerIcon={<Assignment />}
                  tagline={schoolTagline}
                  showSurveyHeader={true}
                  showSurveyProgress={false}
                  compactSurveyLayout={true}
                  sportName={surveyResponse?.sport?.name || ''}
                  coachName={selectedCoach?.name || ''}
                  seasonName={seasonLabel}
                  headLine={respondentTypeLabel}
                >
                  {this.renderCoachSelect()}
                </MobilePage>
              </div>
            );
          default:
            return (
              <span>
                Something is wrong. No respondent type option provided.
              </span>
            );
        }
      }

      private renderCoachSelect() {
        const { surveyResponse, loadingSurvey } = this.props;
        const { selectedCoachId } = this.state;
        if (surveyResponse === undefined) {
          if (loadingSurvey) {
            return <LinearProgress variant="query" />;
          }
          return <h1>Oops... something went wrong!!</h1>;
        }
        return (
          <div style={{ width: "100%" }}>
              <div className={this.props.classes.infoStrip}>
              Your feedback is important to us. Please take a moment to thoughtfully answer the following questions so we can better understand your perspective.
            </div>

              <div
                className={this.props.classes.sectionCard}
                style={{
                  background: selectedCoachId !== undefined ? "#FDFDFD" : undefined,
                }}
              >
                <div className={this.props.classes.sectionTitle}>
                  {this.props.schoolData?.isAdSurvey ? "Select AD" : "Select Coach"}
                </div>
              {this.renderCoachRadioGroup(surveyResponse)}
            </div>

            {this.props.collectRespondentName && (
              <div
                className={this.props.classes.sectionCard}
                style={{
                  background:
                    this.props.respondentName.trim() !== "" ? "#FDFDFD" : undefined,
                }}
              >
                <div className={this.props.classes.sectionTitle}>
                  First and Last Name
                </div>
                <TextField
                  className={this.props.classes.textFieldRoot}
                  fullWidth={true}
                  variant="outlined"
                  placeholder="Enter your first and last name"
                  value={this.props.respondentName}
                  onChange={this.props.respondentNameChange}
                />
              </div>
            )}

            <div className={this.props.classes.actionBar}>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginLeft: "auto" }}>
              {this.renderButtonOrFinished(surveyResponse)}
            </div>
            </div>
          </div>
        );
      }

      private renderButtonOrFinished(
        surveyResponse: ISurveyRespondentResponse
      ) {
        const {
          code,
          collectRespondentName,
          responseSource,
          finishedSurveySections,
        } = this.props;
        const { selectedCoachId } = this.state;

        const finished =
          surveyResponse.coaches.filter(
            (c) =>
              !finishedSurveySections.find(
                (fS) => fS.coachId === c.id && fS.code === code
              )
          ).length === 0;
        if (finished) {
          switch (responseSource) {
            case ResponseSource.student:
            case ResponseSource.coach:
            case ResponseSource.parent:
              return <span>Thanks for your feedback</span>;
            default:
              return <span>Oops… something went wrong</span>;
          }
        }

        return (
          <Button
            disabled={
              selectedCoachId === undefined ||
              (collectRespondentName && this.props.respondentName.trim() === "")
            }
            color="primary"
            variant="contained"
            component={Link}
            onClick={this.nextButtonClick}
            {...({
              to: `/${ResponseSource[responseSource]}/${code}/${selectedCoachId}/1`,
            } as any)}
            className={`${this.props.classes.button} ${this.props.classes.plainButtonLabel}`}
          >
            Next
            <ArrowForward style={{ fontSize: 14, marginLeft: 6 }} />
          </Button>
        );
      }

      private nextButtonClick = () => {
        window.scroll(0, 0);
      };

      private renderCoachRadioGroup(surveyResponse: ISurveyRespondentResponse) {
        const { selectedCoachId } = this.state;
        return (
          <RadioGroup
            aria-label="Gender"
            name="gender1"
            value={selectedCoachId ? selectedCoachId.toString() : undefined}
            onChange={this.selectCoachId}
            style={{ marginTop: 2 }}
          >
            {surveyResponse.coaches.map((coach) => this.renderCoach(coach))}
          </RadioGroup>
        );
      }

      private renderCoach(coach: ISanitizedCoach) {
        const { finishedSurveySections, code, classes } = this.props;
        // check if coach is in finished section
        const coachFinished = !!finishedSurveySections.find(
          (fS) => fS.coachId === coach.id && fS.code === code
        );
        return (
          <FormControlLabel
            key={coach.id}
            disabled={coachFinished}
            value={coach.id.toString()}
            control={<Radio size="small" className={classes.radio} />}
            label={<span className={classes.coachName}>{coach.name}</span>}
            className={classes.formControlRow}
          />
        );
      }

      private selectCoachId = (e: React.ChangeEvent<{}>, value: string) => {
        // console.log(e.target.value);
        this.setState({
          selectedCoachId: value ? parseInt(value, 10) : undefined,
        });
      };
    }
  )
);



// WEBPACK FOOTER //
// ./src/pages/respondent/ChooseCoachPage.tsx